// Endpoints de configuração — `/v1/metricas/config/*` (CONTRATO.md §6.8).
//
// Leitura GERENTE, escrita GESTOR (+ `@Audit` no back). A view aplica
// `skip: true` em TODAS as queries quando `canConfigure` é falso.
//
// ⚠️ Três coisas que eu tinha suposto errado antes do contrato sair, e que estão
// certas aqui:
//  1. `config/limites` é **PATCH**, não PUT.
//  2. **Não existe `config/pesquisa`** — os campos de satisfação vivem em
//     `MetricsSettings`, então o card de pesquisa também faz PATCH em `limites`.
//  3. **Não existe CRUD de alerta.** "Alertas" é um card sobre `MetricsSettings`.
//
// ⚠️ A escrita de `Store.businessHours` NÃO mora neste módulo: continua sendo do
// módulo `stores`. Aqui só a opção de desconto e os feriados.
import { api } from '@/lib/api';
import type {
  MetricsCatalogItemDto,
  MetricsCollection,
  MetricsFunnelStageConfigDto,
  MetricsHolidayDto,
  MetricsLeadSourceDto,
  MetricsLeadSourceRuleDto,
  MetricsSettingsDto,
  MetricsSlaPolicyDto,
} from '../model';
import { METRICS_PREFIX } from './shared';

const CONFIG = `${METRICS_PREFIX}/config`;

/** `storeId` ausente = configuração da REDE; preenchido = override da loja. */
export interface ConfigScopeArgs {
  storeId?: string;
}

/** Corpo comum dos 4 catálogos. `code` nunca muda depois de criado. */
export interface CatalogUpsertArgs extends ConfigScopeArgs {
  id?: string;
  /**
   * OBRIGATÓRIO na criação — o back responde `400 Validation failed` sem ele.
   * NUNCA enviado no PATCH: o `code` é a chave que a instrumentação usa e
   * renomeá-lo quebraria o write-path em silêncio.
   */
  code?: string;
  label?: string;
  active?: boolean;
  position?: number;
  /**
   * OBRIGATÓRIO no PATCH (optimistic locking) — sem ele o back devolve
   * `400 Validation failed` com `path: ["version"]`, e desativar um item pela UI
   * falhava sempre. Descoberto exercitando a API real: o MSW aceitava sem.
   */
  version?: number;
}

export const metricsConfigApi = api.injectEndpoints({
  endpoints: (build) => ({
    /* ── Limites / satisfação / alertas: tudo em MetricsSettings ──────────── */
    metricsConfigSettings: build.query<MetricsSettingsDto, ConfigScopeArgs | void>({
      query: (args) => ({ url: `${CONFIG}/limites`, params: args ?? undefined }),
      providesTags: ['MetricsThreshold'],
    }),
    metricsConfigSaveSettings: build.mutation<
      MetricsSettingsDto,
      Partial<MetricsSettingsDto> & ConfigScopeArgs
    >({
      query: (body) => ({ url: `${CONFIG}/limites`, method: 'PATCH', body }),
      // Limiar muda COMO o agregado é calculado (faixas, abandono, reabertura).
      invalidatesTags: ['MetricsThreshold', 'MetricsSurveyConfig', 'MetricsAggregate'],
    }),
    metricsConfigBusinessHoursOptions: build.query<
      { businessHoursDefault: boolean },
      ConfigScopeArgs | void
    >({
      query: (args) => ({ url: `${CONFIG}/horario-comercial/opcoes`, params: args ?? undefined }),
      providesTags: ['MetricsBusinessHours'],
    }),

    /* ── Metas de SLA ─────────────────────────────────────────────────────── */
    metricsConfigSlaPolicies: build.query<MetricsCollection<MetricsSlaPolicyDto>, void>({
      query: () => `${CONFIG}/sla`,
      providesTags: ['MetricsSlaConfig'],
    }),
    metricsConfigSaveSlaPolicy: build.mutation<
      MetricsSlaPolicyDto,
      Partial<MetricsSlaPolicyDto> & { id?: string }
    >({
      // POST cria; PUT atualiza uma existente. O ESCOPO nunca é editável — mover
      // a política faria a aderência já calculada apontar para meta que não valeu.
      query: ({ id, ...body }) => ({
        url: id ? `${CONFIG}/sla/${id}` : `${CONFIG}/sla`,
        method: id ? 'PUT' : 'POST',
        body,
      }),
      invalidatesTags: ['MetricsSlaConfig', 'MetricsAggregate'],
    }),

    /* ── Catálogos (sem exclusão: só `active: false`) ─────────────────────── */
    metricsConfigLossReasons: build.query<MetricsCollection<MetricsCatalogItemDto>, ConfigScopeArgs | void>({
      query: (args) => ({ url: `${CONFIG}/motivos-perda`, params: args ?? undefined }),
      providesTags: ['MetricsLossReason'],
    }),
    metricsConfigSaveLossReason: build.mutation<MetricsCatalogItemDto, CatalogUpsertArgs>({
      query: ({ id, ...body }) => ({
        url: id ? `${CONFIG}/motivos-perda/${id}` : `${CONFIG}/motivos-perda`,
        method: id ? 'PATCH' : 'POST',
        body,
      }),
      invalidatesTags: ['MetricsLossReason'],
    }),

    metricsConfigTransferReasons: build.query<
      MetricsCollection<MetricsCatalogItemDto>,
      ConfigScopeArgs | void
    >({
      query: (args) => ({ url: `${CONFIG}/transferencias/motivos`, params: args ?? undefined }),
      providesTags: ['MetricsLossReason'],
    }),

    metricsConfigStages: build.query<
      MetricsCollection<MetricsFunnelStageConfigDto>,
      ConfigScopeArgs | void
    >({
      query: (args) => ({ url: `${CONFIG}/etapas`, params: args ?? undefined }),
      providesTags: ['MetricsStage'],
    }),
    metricsConfigSaveStage: build.mutation<
      MetricsFunnelStageConfigDto,
      // `rottingDays: null` LIMPA o prazo — por isso o tipo aceita null e não só
      // ausência do campo.
      CatalogUpsertArgs & { kind?: string; isEntry?: boolean; rottingDays?: number | null }
    >({
      query: ({ id, ...body }) => ({
        url: id ? `${CONFIG}/etapas/${id}` : `${CONFIG}/etapas`,
        method: id ? 'PATCH' : 'POST',
        body,
      }),
      invalidatesTags: ['MetricsStage', 'MetricsAggregate'],
    }),
    metricsConfigReorderStages: build.mutation<
      MetricsCollection<MetricsFunnelStageConfigDto>,
      { ids: string[]; storeId?: string }
    >({
      query: (body) => ({ url: `${CONFIG}/etapas/ordem`, method: 'PUT', body }),
      invalidatesTags: ['MetricsStage', 'MetricsAggregate'],
    }),

    metricsConfigOrigins: build.query<MetricsCollection<MetricsLeadSourceDto>, ConfigScopeArgs | void>({
      query: (args) => ({ url: `${CONFIG}/origens`, params: args ?? undefined }),
      providesTags: ['MetricsOrigin'],
    }),
    metricsConfigSaveOrigin: build.mutation<
      MetricsLeadSourceDto,
      CatalogUpsertArgs & { kind?: string }
    >({
      query: ({ id, ...body }) => ({
        url: id ? `${CONFIG}/origens/${id}` : `${CONFIG}/origens`,
        method: id ? 'PATCH' : 'POST',
        body,
      }),
      invalidatesTags: ['MetricsOrigin'],
    }),
    metricsConfigOriginRules: build.query<
      MetricsCollection<MetricsLeadSourceRuleDto>,
      { leadSourceId?: string } | void
    >({
      query: (args) => ({ url: `${CONFIG}/origens/regras`, params: args ?? undefined }),
      providesTags: ['MetricsOrigin'],
    }),

    /* ── Feriados: o ÚNICO com DELETE real ────────────────────────────────── */
    metricsConfigHolidays: build.query<MetricsCollection<MetricsHolidayDto>, ConfigScopeArgs | void>({
      query: (args) => ({ url: `${CONFIG}/feriados`, params: args ?? undefined }),
      providesTags: ['MetricsBusinessHours'],
    }),
    metricsConfigSaveHoliday: build.mutation<
      MetricsHolidayDto,
      Partial<MetricsHolidayDto> & { id?: string }
    >({
      query: ({ id, ...body }) => ({
        url: id ? `${CONFIG}/feriados/${id}` : `${CONFIG}/feriados`,
        method: id ? 'PATCH' : 'POST',
        body,
      }),
      invalidatesTags: ['MetricsBusinessHours', 'MetricsAggregate'],
    }),
    /** Feriado não classifica atendimento nenhum — por isso some de verdade. */
    metricsConfigDeleteHoliday: build.mutation<{ deleted: true }, { id: string }>({
      query: ({ id }) => ({ url: `${CONFIG}/feriados/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MetricsBusinessHours', 'MetricsAggregate'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useMetricsConfigSettingsQuery,
  useMetricsConfigSaveSettingsMutation,
  useMetricsConfigBusinessHoursOptionsQuery,
  useMetricsConfigSlaPoliciesQuery,
  useMetricsConfigSaveSlaPolicyMutation,
  useMetricsConfigLossReasonsQuery,
  useMetricsConfigSaveLossReasonMutation,
  useMetricsConfigTransferReasonsQuery,
  useMetricsConfigStagesQuery,
  useMetricsConfigSaveStageMutation,
  useMetricsConfigReorderStagesMutation,
  useMetricsConfigOriginsQuery,
  useMetricsConfigSaveOriginMutation,
  useMetricsConfigOriginRulesQuery,
  useMetricsConfigHolidaysQuery,
  useMetricsConfigSaveHolidayMutation,
  useMetricsConfigDeleteHolidayMutation,
} = metricsConfigApi;
